/**
 * Cookie consent.
 *
 * Shopify's banner is replaced by a smaller one. The reason is presentation
 * only - Shopify's is four lines of grey prose across a full-bleed editorial
 * homepage - so the substance is unchanged: consent is recorded through
 * Shopify's Customer Privacy API, exactly as its own banner does. Nothing
 * here fakes a decision or remembers one locally.
 *
 * Shopify's banner is hidden by adding .owr-consent-ready, and that class is
 * added only at the moment this script is certain it will show its own.
 * The default is therefore Shopify's banner, not ours: if this file fails to
 * load, is blocked, or the privacy API never arrives, the visitor still gets
 * asked. Hiding it from CSS up front would have meant a broken script = no
 * consent banner at all, for everyone, silently.
 *
 * It appears only when Shopify says consent is required and not yet given.
 * An Indian visitor never sees it - India is not a region Shopify asks for
 * consent in, which is also why it appeared today and not before: until the
 * International market existed, every visitor was Indian as far as Shopify
 * was concerned.
 *
 * Accept and Decline are equally prominent and both are one click. A decline
 * that takes more effort than an accept is not a free choice, and several
 * regulators say so plainly.
 *
 * It deliberately offers no per-category toggles. Accept-all and decline-all
 * are the two choices, which is defensible where the alternative is a
 * 'manage preferences' screen nobody reads - but it IS a judgement call, and
 * a stricter reading of GDPR wants granular control. Worth revisiting before
 * selling seriously into the EU.
 */
(function () {
  'use strict';

  var SHOWN = false;

  function build(onChoice) {
    var el = document.createElement('div');
    el.className = 'owr-consent';
    el.setAttribute('role', 'dialog');
    el.setAttribute('aria-live', 'polite');
    el.setAttribute('aria-label', 'Cookie consent');
    el.innerHTML =
      '<p class="owr-consent__text">We use cookies to improve your experience. ' +
      '<a class="owr-consent__link" href="/policies/privacy-policy">Privacy</a></p>' +
      '<div class="owr-consent__actions">' +
      '<button type="button" class="owr-consent__btn owr-consent__btn--ghost" data-consent="no">Decline</button>' +
      '<button type="button" class="owr-consent__btn" data-consent="yes">Accept</button>' +
      '</div>';

    el.addEventListener('click', function (e) {
      var btn = e.target.closest ? e.target.closest('[data-consent]') : null;
      if (!btn) return;
      onChoice(btn.getAttribute('data-consent') === 'yes', el);
    });

    document.body.appendChild(el);
    requestAnimationFrame(function () { el.classList.add('is-in'); });
    return el;
  }

  function dismiss(el) {
    if (!el || el.dataset.going) return;
    el.dataset.going = '1';
    el.classList.remove('is-in');
    setTimeout(function () {
      if (el.parentNode) el.parentNode.removeChild(el);
    }, 220);
  }

  function start() {
    if (SHOWN) return;
    if (!window.Shopify || !window.Shopify.loadFeatures) return;

    window.Shopify.loadFeatures(
      [{ name: 'consent-tracking-api', version: '0.1' }],
      function (error) {
        var privacy = window.Shopify && window.Shopify.customerPrivacy;
        if (error || !privacy || typeof privacy.setTrackingConsent !== 'function') {
          // Leave Shopify's banner alone. Being asked twice would be a bug;
          // not being asked at all would be a breach.
          return;
        }

        // Shopify decides whether consent is needed at all - the region,
        // whether a choice was already made, how long it lasts. Asking it
        // rather than guessing is the point of using the API.
        var needed = true;
        try {
          if (typeof privacy.shouldShowBanner === 'function') needed = privacy.shouldShowBanner();
        } catch (e) { /* if it will not say, ask anyway */ }
        if (!needed) return;

        SHOWN = true;

        // Only now is Shopify's banner hidden: this script is past every way
        // it could have failed and is about to put its own on screen.
        document.documentElement.classList.add('owr-consent-ready');

        build(function (accepted, el) {
          var consent = {
            analytics: accepted,
            marketing: accepted,
            preferences: accepted,
            sale_of_data: accepted
          };
          try {
            privacy.setTrackingConsent(consent, function () { dismiss(el); });
          } catch (e) {
            dismiss(el);
          }
          // The callback is not guaranteed to fire. The banner should not
          // outlive the click either way.
          setTimeout(function () { dismiss(el); }, 1200);
        });
      }
    );
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
